import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useIsAuthenticated } from '@/features/auth/store/authStore'
import type { PageResponse } from '@/shared/api/PageResponse'

import { notificationsApi } from '../api/notificationsApi'
import type {
  NotificationResponse,
  NotificationTab,
  UnreadCountResponse,
} from '../types/notification.types'

export const notificationsKeys = {
  all: ['notifications'] as const,
  list: (page: number, size: number, tab?: NotificationTab) =>
    ['notifications', 'list', tab ?? 'ALL', page, size] as const,
  unread: () => ['notifications', 'unread-count'] as const,
}

type Lists = [readonly unknown[], PageResponse<NotificationResponse> | undefined][]

/**
 * Polling del contador de la campana. 60s entre refetch (staleTime 30s para que
 * dos componentes que comparten el dato no disparen request duplicada).
 * Solo si el usuario está autenticado — anónimos no tienen campana.
 */
export function useUnreadCountQuery() {
  const isAuthenticated = useIsAuthenticated()
  return useQuery<UnreadCountResponse>({
    queryKey: notificationsKeys.unread(),
    queryFn: () => notificationsApi.unreadCount(),
    enabled: isAuthenticated,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  })
}

/**
 * Listado de una pestaña (o de todos, sin `tab`). Se carga lazy (`enabled` lo controla quien
 * lo usa — típicamente «está abierta la campana»).
 */
export function useNotificationsListQuery({
  page = 0,
  size = 10,
  enabled = true,
  tab,
}: {
  page?: number
  size?: number
  enabled?: boolean
  tab?: NotificationTab
} = {}) {
  const isAuthenticated = useIsAuthenticated()
  return useQuery<PageResponse<NotificationResponse>>({
    queryKey: notificationsKeys.list(page, size, tab),
    queryFn: () => notificationsApi.list(page, size, tab),
    enabled: isAuthenticated && enabled,
    staleTime: 10_000,
  })
}

/** Pestaña de un aviso; los de un servidor anterior (sin `tab`) cuentan como «Tus partidas». */
export function tabOf(n: Pick<NotificationResponse, 'tab'>): NotificationTab {
  return n.tab === 'NEWS' ? 'NEWS' : 'ACTIVITY'
}

/**
 * Optimistic común a marcar como leídos: pone `readAt` en los avisos que cumplan `match` en
 * todas las listas cacheadas y descuenta del contador (total y de su pestaña) los que estaban
 * sin leer. Devuelve lo necesario para deshacerlo si el servidor falla.
 */
async function markReadOptimistic(
  queryClient: QueryClient,
  match: (n: NotificationResponse) => boolean,
) {
  await queryClient.cancelQueries({ queryKey: notificationsKeys.all })
  const previousLists: Lists = queryClient.getQueriesData<PageResponse<NotificationResponse>>({
    queryKey: ['notifications', 'list'],
  })
  const previousUnread = queryClient.getQueryData<UnreadCountResponse>(notificationsKeys.unread())

  // El mismo aviso puede estar en varias listas cacheadas (todas / su pestaña): contarlo una vez.
  const newlyRead = new Map<number, NotificationTab>()
  const now = new Date().toISOString()
  queryClient.setQueriesData<PageResponse<NotificationResponse>>(
    { queryKey: ['notifications', 'list'] },
    (old) => {
      if (!old) return old
      return {
        ...old,
        content: old.content.map((n) => {
          if (n.readAt !== null || !match(n)) return n
          newlyRead.set(n.id, tabOf(n))
          return { ...n, readAt: now }
        }),
      }
    },
  )
  if (previousUnread && newlyRead.size > 0) {
    const tabs = [...newlyRead.values()]
    const activity = tabs.filter((t) => t === 'ACTIVITY').length
    const news = tabs.length - activity
    queryClient.setQueryData<UnreadCountResponse>(notificationsKeys.unread(), {
      count: Math.max(0, previousUnread.count - tabs.length),
      ...(previousUnread.activity !== undefined && {
        activity: Math.max(0, previousUnread.activity - activity),
      }),
      ...(previousUnread.news !== undefined && { news: Math.max(0, previousUnread.news - news) }),
    })
  }
  return { previousLists, previousUnread }
}

function rollback(
  queryClient: QueryClient,
  ctx: { previousLists: Lists; previousUnread: UnreadCountResponse | undefined } | undefined,
) {
  if (!ctx) return
  ctx.previousLists.forEach(([key, value]) => queryClient.setQueryData(key, value))
  if (ctx.previousUnread !== undefined) {
    queryClient.setQueryData(notificationsKeys.unread(), ctx.previousUnread)
  }
}

/** Marca un aviso como leído. Optimistic con rollback; al terminar, reconcilia con el servidor. */
export function useMarkAsReadMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => notificationsApi.markAsRead(id),
    onMutate: (id) => markReadOptimistic(queryClient, (n) => n.id === id),
    onError: (_err, _id, ctx) => rollback(queryClient, ctx),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: notificationsKeys.all })
    },
  })
}

/** Marca varios de una vez (un grupo de novedades al desplegarlo). */
export function useMarkSeveralAsReadMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ids: number[]) => notificationsApi.markSeveralAsRead(ids),
    onMutate: (ids) => markReadOptimistic(queryClient, (n) => ids.includes(n.id)),
    onError: (_err, _ids, ctx) => rollback(queryClient, ctx),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: notificationsKeys.all })
    },
  })
}

/** «Marcar todo como leído» de una pestaña (o de todos, sin `tab`). Optimistic con rollback. */
export function useMarkAllAsReadMutation(tab?: NotificationTab) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => notificationsApi.markAllAsRead(tab),
    onMutate: () => markReadOptimistic(queryClient, (n) => !tab || tabOf(n) === tab),
    onError: (_err, _v, ctx) => rollback(queryClient, ctx),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: notificationsKeys.all })
    },
  })
}

/**
 * Borra los avisos LEÍDOS de una pestaña (o de todos). Sin optimistic: invalidar es más simple
 * que filtrar cada página cacheada. El servidor es idempotente.
 */
export function useDeleteAllReadMutation(tab?: NotificationTab) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => notificationsApi.deleteAllRead(tab),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: notificationsKeys.all })
    },
  })
}
